import React, { useState, useEffect } from 'react'
import { Star } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

const FALLBACK_TESTIMONIALS = [
  {
    id: 't1',
    name: 'Morten Jensen',
    company: 'Københavns Erhvervspark',
    rating: 5,
    text: { 
      da: '<p>Wecas ApS har stået for vores kontorrengøring i over 3 år. De leverer altid et fejlfrit resultat, og deres stabilitet og fleksibilitet er uovertruffen. Kan varmt anbefales!</p>',
      en: '<p>Wecas ApS has been in charge of our office cleaning for over 3 years. They always deliver a flawless result, and their stability and flexibility are unmatched. Highly recommended!</p>'
    }
  },
  {
    id: 't2',
    name: 'Sarah Lindgaard',
    company: 'Klinik For Sundhed',
    rating: 5,
    text: { 
      da: '<p>Højeste standard af renlighed er afgørende for vores klinik. Wecas leverer en utrolig grundig og pålidelig service, der lever fuldt op til de strenge hygiejnekrav.</p>',
      en: '<p>The highest standard of cleanliness is crucial for our clinic. Wecas delivers an incredibly thorough and reliable service that fully meets the strict hygiene requirements.</p>'
    }
  },
  {
    id: 't3',
    name: 'Thomas Poulsen',
    company: 'TP Byg & Anlæg',
    rating: 5,
    text: { 
      da: '<p>Deres byggepladsservice har sparet os for meget tid. De holder pladsen ryddelig og sikker, og slutrengøringen inden aflevering var helt i top.</p>',
      en: '<p>Their construction site service has saved us a lot of time. They keep the site tidy and safe, and the final cleaning before handover was top-notch.</p>'
    }
  }
]

export default function Testimonials() {
  const { language, t } = useLanguage()
  const [testimonials, setTestimonials] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/testimonials')
        if (response.ok) {
          const data = await response.json()
          if (Array.isArray(data) && data.length > 0) {
            setTestimonials(data)
          } else {
            setTestimonials(FALLBACK_TESTIMONIALS)
          }
        } else {
          setTestimonials(FALLBACK_TESTIMONIALS)
        }
      } catch (err) {
        console.error('Error fetching testimonials:', err)
        setTestimonials(FALLBACK_TESTIMONIALS)
      } finally {
        setLoading(false)
      }
    }

    fetchTestimonials()
  }, [])

  return (
    <section id="anmeldelser" className="relative bg-[#FAF8F5] py-12 md:py-16 overflow-hidden border-t border-[#E6DFD3]">
      {/* Background Image with elegant light overlay */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <img 
          src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/99557a41-4297-4198-ac44-fb5c9d0f9512.jpg" 
          alt="Danish beach background" 
          className="w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#FAF8F5] via-[#FAF8F5]/90 to-[#FAF8F5]" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h2 className="font-marcellus text-2xl sm:text-3xl md:text-4xl font-light tracking-[0.08em] text-[#2C302E] leading-tight mb-6 uppercase">
            {language === 'da' ? (
              <>Hvad vores <br /> <span className="font-normal text-[#a78458]">samarbejdspartnere</span> siger</>
            ) : (
              <>What our <br /> <span className="font-normal text-[#a78458]">partners</span> say</>
            )}
          </h2>
          <p className="text-sm sm:text-base text-[#2C302E]/70 leading-relaxed max-w-xl mx-auto">
            {language === 'da'
              ? 'Troværdighed og stabilitet er fundamentet for vores forretning. Her er et udpluk af udtalelser fra virksomheder, vi samarbejder med.'
              : 'Credibility and stability are the foundation of our business. Here is a selection of testimonials from companies we collaborate with.'}
          </p>
        </div>

        {/* Testimonials Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#a78458] border-t-transparent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => {
              const textHtml = testimonial.text?.[language] || testimonial.text?.da || testimonial.text || ''
              return (
                <div
                  key={testimonial.id}
                  className="bg-white rounded-none p-8 border border-[#E6DFD3] relative flex flex-col justify-between transition-all duration-300 hover:shadow-lg hover:border-[#a78458]/30"
                >
                  <div>
                    {/* Stars */}
                    <div className="flex gap-1 mb-6">
                      {[...Array(testimonial.rating || 5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-[#a78458] text-[#a78458]" />
                      ))}
                    </div>

                    {/* Text */}
                    <div
                      className="prose prose-stone text-sm sm:text-base text-[#2C302E]/80 leading-relaxed mb-6
                        [&>p]:mb-0"
                      dangerouslySetInnerHTML={{ __html: textHtml }}
                    />
                  </div>

                  {/* Author */}
                  <div className="pt-6 border-t border-[#E6DFD3]/60 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-none bg-[#a78458]/5 flex items-center justify-center font-marcellus font-bold text-sm text-[#a78458]">
                      {testimonial.name ? testimonial.name.charAt(0) : 'W'}
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm text-[#2C302E] uppercase tracking-wider">
                        {testimonial.name}
                      </h4>
                      <p className="text-xs text-[#2C302E]/60">
                        {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}
